import { getNumericValidationInfo } from './ValidationHelperFunctions';

import type {
  IConfigurationValue,
  IConfigurationVariable,
  INumericButtonProps,
  INumericValueUpdateProps,
  IValidityInfo,
} from '../../types';

const lowerbound = { name: 'BoundValue', value: Number.MIN_SAFE_INTEGER } as IConfigurationValue;
const upperbound = { name: 'BoundValue', value: Number.MAX_SAFE_INTEGER } as IConfigurationValue;

/**
 * This method validate the numeric value and return the message if not valid
 * @param {IConfigurationVariable} variable configuration variable
 * @param {string} value
 * @param {boolean} isOptional when true, 0 and empty are treated as invalid ("unassigned")
 * @returns {IValidityInfo} return the validation message
 */
export function getValidationInfo(
  variable: IConfigurationVariable,
  value: string,
  isOptional: boolean = false
): IValidityInfo {
  const info = getNumericValidationInfo(variable, Number(value), isOptional);
  if (value === '') {
    return { ...info, valid: !isOptional };
  }
  const valueSplit = value.split('.');
  if (
    valueSplit.length === 2 &&
    (valueSplit[1].length === 0 || valueSplit[1].length > Number(variable.scale))
  ) {
    return { ...info, valid: false };
  }
  return info;
}

/**
 * This method returns the lower or uppper value
 * @param {INumericInputValues} value numeric value object
 * @param {boolean} lower should return lower value or not
 * @returns {number} it returns the lower or upper value
 */
export function getValue(value: IConfigurationValue, lower: boolean = true): number {
  if ('name' in value) {
    return Number(value.value);
  } else {
    return lower ? Number(value.lower) : Number(value.upper);
  }
}

/**
 * This method returns the lower value from the values array
 * @param {IConfigurationValue[]} values values array
 * @returns {number} return lower value
 */
export function getLower(values: IConfigurationValue[]): number {
  let value = upperbound;
  if (values && Array.isArray(values)) {
    values.forEach((v) => {
      const pv = getValue(value),
        cv = getValue(v);
      if (!Number.isNaN(cv) && pv >= cv) {
        value = v;
      }
    });
  }

  return value.type === 'BoundValue' ? Number.MIN_SAFE_INTEGER : getValue(value);
}

/**
 * This method returns the upper value from the values array
 * @param {IConfigurationValue[]} values values array
 * @returns {number} return upper value
 */
export function getUpper(values: IConfigurationValue[]) {
  let value = lowerbound;
  if (values && Array.isArray(values)) {
    values.forEach((v) => {
      const pv = getValue(value, false),
        cv = getValue(v, false);
      if (!Number.isNaN(cv) && pv <= cv) {
        value = v;
      }
    });
  }

  return value.type === 'BoundValue' ? Number.MAX_SAFE_INTEGER : getValue(value, false);
}

/**
 * THis method check and restrict to enter the invalid characters in the numeric text box
 * @param {React.KeyboardEvent<HTMLDivElement> } e input event
 * @returns {void}
 */
export function blockInvalidChar(e: React.KeyboardEvent<HTMLDivElement>): void {
  if (['e', 'E', '+', '-'].includes(e.key)) {
    e.preventDefault();
  }
}

/**
 * This method check whether the current value is valid assignment or not
 * @param {string} value current value
 * @param {string} newValue new value
 * @param {boolean} valid is valid flag
 * @returns {void}
 */
export function isValidAssignment(value: string, newValue: string, valid: boolean) {
  let validAssignment = valid;
  if (valid && value && newValue && Number(value) === Number(newValue)) {
    validAssignment = false;
  }
  return validAssignment;
}

/**
 * This method returns the value by increment based on  step value
 * @param {string} value current value
 * @param {string} increment new value
 * @param {boolean} step is valid flag
 * @returns {void}
 */
export function getUpdatedValue(value: string, increment: boolean, step: number) {
  return Number(value) + (increment ? 1 : -1) * step;
}

/**
 * This method returns the class value to update the increment and decreant button color
 * @param {INumericButtonProps} props input props
 */
export function getButtonProperties(props: INumericButtonProps) {
  const { value, lower, upper, step, disabled } = props;
  const decDisabled = Number(value) - lower < step || !!disabled;
  const incDisabled = upper - Number(value) < step || !!disabled;
  return {
    dec: {
      disabled: decDisabled,
      className: decDisabled ? 'btn-decrement-disabled' : 'btn-decrement',
    },
    inc: {
      disabled: incDisabled,
      className: incDisabled ? 'btn-decrement-disabled' : 'btn-increment',
    },
  };
}

/**
 * This method returns the valid minimum value from the values array
 * @param {IConfigurationVariable} variable values array
 * @param {number} newValue new value
 * @returns {number} return minimum value
 */
export function getMinValue(variable: IConfigurationVariable, newValue: number): number {
  const lowers: number[] = variable.values.map((v) => Number(v.lower ?? v.value));
  lowers.sort((a: number, b: number) => a - b);
  // single-interval starting at 0 (e.g. [0-9999]) — no lower bound > 0 exists, so return 1 as the minimum valid positive value
  if (newValue === 0 && lowers.length === 1 && lowers[0] === 0) {
    return 1;
  }
  const low = lowers.find((l) => l > newValue);
  return low ?? -1;
}

/**
 * This method returns the maximum value from the values array
 * @param {IConfigurationVariable} variable values array
 * @param {number} newValue new value
 * @returns {number} return macimum value
 */
export function getMaxValue(variable: IConfigurationVariable, newValue: number) {
  const uppers: number[] = variable.values.map((v) => Number(v.upper));
  uppers.sort((a: number, b: number) => b - a);
  const max = uppers.find((l) => l < newValue);
  return max ?? -1;
}

/**
 * THis method validate the current value and update to to the state.
 * @param {INumericValueUpdateProps} props input props
 * @returns {void}
 */
export function onValueUpdate(props: INumericValueUpdateProps): void {
  const {
    increment,
    internalNumericValue,
    variable,
    step,
    setRangeValidity,
    debounceOnChange,
    setInternalNumericValue,
  } = props;
  let newValue: number = getUpdatedValue(internalNumericValue, increment, step);
  const currentValidity = getValidationInfo(variable, newValue.toString());

  if (currentValidity.valid) {
    setRangeValidity('');
  } else {
    if (increment) {
      newValue = getMinValue(variable, newValue);
      if (!newValue || newValue < 0) {
        newValue = getUpper(variable.values);
      }
    } else {
      newValue = getMaxValue(variable, newValue);
      if (!newValue || newValue < 0) {
        newValue = getLower(variable.values);
      }
    }
    setRangeValidity(getValidationInfo(variable, newValue.toString()).validValues.join(', '));
  }

  setInternalNumericValue(newValue.toString());
  //call debounce hook method to set the timeout
  debounceOnChange(newValue.toString());
}
